import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Trash2, X, Pencil, Globe, MapPin, Target,
  Megaphone, Building2, Search, TrendingUp, AlertTriangle, ChevronDown, FileText
} from "lucide-react";
import type { InvestorFirm } from "@shared/schema";

const INVESTMENT_STAGES = ["Angel", "Seed", "Series A", "Series B+"];

type SortField = "firmName" | "hqLocation" | "investmentFocus" | "recentAnnouncements" | "notes";

export default function Investors() {
  const { user } = useAuth();
  const isOwner = ["matt@mattrebeiro.com", "matt@learnpdi.com"].includes(user?.email || "");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editingFirm, setEditingFirm] = useState<InvestorFirm | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("firmName");
  const [filterHq, setFilterHq] = useState("");
  const [filterFocus, setFilterFocus] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const [form, setForm] = useState({
    firmName: "",
    website: "",
    hqLocation: "",
    investmentStage: "",
    investmentFocus: "",
    recentAnnouncements: "",
    notes: "",
  });

  const { data: firms, isLoading } = useQuery<InvestorFirm[]>({
    queryKey: ["/api/investors"],
    queryFn: async () => {
      const res = await fetch("/api/investors", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await fetch("/api/investors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/investors"] });
      resetForm();
      toast({ title: "Firm added to directory" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof form }) => {
      const res = await fetch(`/api/investors/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/investors"] });
      resetForm();
      toast({ title: "Firm updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/investors/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/investors"] });
      toast({ title: "Firm removed from directory" });
    },
  });

  function resetForm() {
    setForm({ firmName: "", website: "", hqLocation: "", investmentStage: "", investmentFocus: "", recentAnnouncements: "", notes: "" });
    setEditingFirm(null);
    setShowForm(false);
  }

  function startEdit(firm: InvestorFirm) {
    setEditingFirm(firm);
    setForm({
      firmName: firm.firmName,
      website: firm.website,
      hqLocation: firm.hqLocation,
      investmentStage: firm.investmentStage,
      investmentFocus: firm.investmentFocus,
      recentAnnouncements: firm.recentAnnouncements,
      notes: firm.notes ?? "",
    });
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingFirm) {
      updateMutation.mutate({ id: editingFirm.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const uniqueHqs = Array.from(new Set((firms || []).map((f) => f.hqLocation).filter(Boolean))).sort();
  const uniqueFocuses = Array.from(new Set((firms || []).map((f) => f.investmentFocus).filter(Boolean))).sort();

  const filtered = (firms || [])
    .filter((f) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        f.firmName.toLowerCase().includes(q) ||
        f.hqLocation.toLowerCase().includes(q) ||
        f.investmentFocus.toLowerCase().includes(q) ||
        f.recentAnnouncements.toLowerCase().includes(q) ||
        (f.notes || "").toLowerCase().includes(q);
      const matchesHq = !filterHq || f.hqLocation === filterHq;
      const matchesFocus = !filterFocus || f.investmentFocus === filterFocus;
      const matchesStage = !filterStage || f.investmentStage === filterStage;
      return matchesSearch && matchesHq && matchesFocus && matchesStage;
    })
    .sort((a, b) => {
      const valA = String(a[sortBy] ?? "").toLowerCase();
      const valB = String(b[sortBy] ?? "").toLowerCase();
      return valA.localeCompare(valB);
    });

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Directory</span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6">Investor Directory</h1>
                  <p className="text-xl text-muted-foreground max-w-2xl mb-4">
                    Browse venture capital firms and startup investors, including their headquarters location and typical investment focus areas.
                  </p>
                  <button
                    onClick={() => setDisclaimerOpen(!disclaimerOpen)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-investor-disclaimer"
                    aria-expanded={disclaimerOpen}
                    aria-controls="investor-disclaimer-content"
                  >
                    <AlertTriangle size={12} aria-hidden="true" />
                    <span className="font-medium">Disclaimer</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${disclaimerOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {disclaimerOpen && (
                    <div id="investor-disclaimer-content" className="mt-3 text-xs text-muted-foreground leading-relaxed max-w-2xl space-y-2" role="note">
                      <p>The information in this directory is provided for general informational purposes only. While reasonable efforts are made to maintain accurate and current listings, we make no representations or warranties regarding the completeness, accuracy, or timeliness of any information, including investor details, investment focus areas, or website links.</p>
                      <p>Inclusion of any investor or firm does not constitute an endorsement, recommendation, solicitation, or affiliation. Investment strategies, criteria, and activities may change at any time and should be independently verified with the respective firm.</p>
                      <p>Nothing contained in this directory should be interpreted as investment advice, fundraising guidance, or a guarantee of investor interest or engagement.</p>
                      <p>Company names and trademarks are the property of their respective owners and are used for identification purposes only.</p>
                    </div>
                  )}
                </div>
                {isOwner && (
                  <Button onClick={() => { resetForm(); setShowForm(true); }} className="shrink-0" data-testid="button-add-firm">
                    <Plus size={16} className="mr-2" /> Add Firm
                  </Button>
                )}
              </div>
            </div>

            {showForm && isOwner && (
              <Card className="border-white/10 p-6 mb-10">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">{editingFirm ? "Edit Firm" : "Add New Firm"}</h3>
                  <button onClick={resetForm} aria-label="Close form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
                </div>
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="inv-firm-name">Firm Name</Label>
                      <Input
                        id="inv-firm-name"
                        value={form.firmName}
                        onChange={(e) => setForm({ ...form, firmName: e.target.value })}
                        placeholder="e.g. Sequoia Capital"
                        className="bg-background border-white/10"
                        required
                        data-testid="input-firm-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-website">Website</Label>
                      <Input
                        id="inv-website"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="e.g. sequoiacap.com"
                        className="bg-background border-white/10"
                        data-testid="input-firm-website"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-hq">HQ Location</Label>
                      <Input
                        id="inv-hq"
                        value={form.hqLocation}
                        onChange={(e) => setForm({ ...form, hqLocation: e.target.value })}
                        placeholder="e.g. Menlo Park, CA"
                        className="bg-background border-white/10"
                        data-testid="input-firm-hq"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-stage">Investment Stage</Label>
                      <select
                        id="inv-stage"
                        value={form.investmentStage}
                        onChange={(e) => setForm({ ...form, investmentStage: e.target.value })}
                        className="w-full bg-background border border-white/10 rounded-md px-3 py-2.5 text-sm min-h-[44px]"
                        data-testid="input-firm-stage"
                      >
                        <option value="">Select stage...</option>
                        {INVESTMENT_STAGES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-focus">Investment Focus</Label>
                      <Input
                        id="inv-focus"
                        value={form.investmentFocus}
                        onChange={(e) => setForm({ ...form, investmentFocus: e.target.value })}
                        placeholder="e.g. B2B SaaS, Fintech"
                        className="bg-background border-white/10"
                        data-testid="input-firm-focus"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-announcements">Updates</Label>
                    <Textarea
                      id="inv-announcements"
                      value={form.recentAnnouncements}
                      onChange={(e) => setForm({ ...form, recentAnnouncements: e.target.value })}
                      placeholder="Latest fund raises, notable investments, partner changes, etc."
                      rows={4}
                      className="bg-background border-white/10"
                      data-testid="input-firm-announcements"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-notes">Notes</Label>
                    <Textarea
                      id="inv-notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Notes shown on the directory listing"
                      rows={3}
                      className="bg-background border-white/10"
                      data-testid="input-firm-notes"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      data-testid="button-submit-firm"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? "Saving..."
                        : editingFirm
                          ? "Update Firm"
                          : "Add Firm"}
                    </Button>
                    <Button type="button" variant="outline" className="border-white/10" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search firms..."
                  aria-label="Search investor firms"
                  className="bg-card border-white/10 pl-10"
                  data-testid="input-search-firms"
                />
              </div>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                aria-label="Filter by investment stage"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[160px] min-h-[44px]"
                data-testid="select-filter-stage"
              >
                <option value="">All Stages</option>
                {INVESTMENT_STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={filterHq}
                onChange={(e) => setFilterHq(e.target.value)}
                aria-label="Filter by HQ location"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-filter-hq"
              >
                <option value="">All Locations</option>
                {uniqueHqs.map((hq) => (
                  <option key={hq} value={hq}>{hq}</option>
                ))}
              </select>
              <select
                value={filterFocus}
                onChange={(e) => setFilterFocus(e.target.value)}
                aria-label="Filter by investment focus"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-filter-focus"
              >
                <option value="">All Investment Focus</option>
                {uniqueFocuses.map((focus) => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortField)}
                aria-label="Sort results"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-sort-by"
              >
                <option value="firmName">Sort: Name (A-Z)</option>
                <option value="hqLocation">Sort: HQ Location</option>
                <option value="investmentFocus">Sort: Investment Focus</option>
                <option value="recentAnnouncements">Sort: Updates</option>
                <option value="notes">Sort: Notes</option>
              </select>
            </div>

            <div aria-live="polite" role="status" className="sr-only">
              {!isLoading && firms ? `${filtered.length} of ${firms.length} firms shown` : "Loading investor directory"}
            </div>

            {isLoading ? (
              <div className="space-y-4" aria-busy="true" aria-label="Loading investor firms">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-white/5 rounded-2xl p-8 animate-pulse">
                    <div className="h-6 bg-white/5 rounded w-1/3 mb-4" />
                    <div className="h-4 bg-white/5 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="space-y-4" role="list" aria-label="Investor firms">
                {filtered.map((firm) => (
                  <Card
                    role="listitem"
                    key={firm.id}
                    className="border-white/5 p-6 md:p-8 hover:border-primary/20 transition-all"
                    data-testid={`card-firm-${firm.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <Building2 size={20} className="text-primary shrink-0" aria-hidden="true" />
                          <h2 className="text-xl font-display font-bold">{firm.firmName}</h2>
                          {firm.investmentStage && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{firm.investmentStage}</span>
                          )}
                          {firm.website && (
                            <a
                              href={firm.website.startsWith("http") ? firm.website : `https://${firm.website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                              data-testid={`link-website-${firm.id}`}
                            >
                              <Globe size={14} aria-hidden="true" /> {firm.website}
                              <span className="sr-only"> — {firm.firmName} (opens in new tab)</span>
                            </a>
                          )}
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
                          {firm.hqLocation && (
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">HQ Location</span>
                                <span className="text-foreground">{firm.hqLocation}</span>
                              </div>
                            </div>
                          )}
                          {firm.investmentStage && (
                            <div className="flex items-start gap-2 text-sm">
                              <TrendingUp size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Investment Stage</span>
                                <span className="text-foreground">{firm.investmentStage}</span>
                              </div>
                            </div>
                          )}
                          {firm.investmentFocus && (
                            <div className="flex items-start gap-2 text-sm">
                              <Target size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Investment Focus</span>
                                <span className="text-foreground">{firm.investmentFocus}</span>
                              </div>
                            </div>
                          )}
                          {firm.recentAnnouncements && (
                            <div className="flex items-start gap-2 text-sm">
                              <Megaphone size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Updates</span>
                                <span className="text-foreground whitespace-pre-wrap">{firm.recentAnnouncements}</span>
                              </div>
                            </div>
                          )}
                          {firm.notes && (
                            <div className="flex items-start gap-2 text-sm">
                              <FileText size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Notes</span>
                                <span className="text-foreground whitespace-pre-wrap">{firm.notes}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {isOwner && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Edit ${firm.firmName}`}
                            onClick={() => startEdit(firm)}
                            data-testid={`button-edit-firm-${firm.id}`}
                          >
                            <Pencil size={14} aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400"
                            aria-label={`Delete ${firm.firmName}`}
                            onClick={() => { if (confirm(`Delete ${firm.firmName}?`)) deleteMutation.mutate(firm.id); }}
                            data-testid={`button-delete-firm-${firm.id}`}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : firms && firms.length > 0 ? (
              <div className="text-center py-16 bg-card border border-white/5 rounded-2xl">
                <Search size={32} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h3 className="text-lg font-semibold mb-2">No results found</h3>
                <p className="text-muted-foreground text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                <Building2 size={40} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h3 className="text-xl font-display font-semibold mb-3">No firms listed yet</h3>
                <p className="text-muted-foreground">The investor directory is being built. Check back soon.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
